import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { landlordAPI } from '../../lib/api';
import {
  Crown,
  Check,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  ShieldCheck,
  Sparkles,
  Infinity as InfinityIcon,
  Gift,
  Ticket,
  X,
  Ban,
} from 'lucide-react';

const LOYALTY_BONUS_DAYS = 36;
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 10;

const money = (amount) => `$${Number(amount || 0).toFixed(2).replace(/\.00$/, '')}`;

export default function LandlordSubscription() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Promo
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discount_percent }
  const [validatingPromo, setValidatingPromo] = useState(false);
  const pollAttemptsRef = useRef(0);

  const fetchSubscription = useCallback(async () => {
    try {
      const { data } = await landlordAPI.getStripeSubscriptionStatus();
      setSubscription(data);
    } catch {
      setSubscription({ status: 'none' });
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await landlordAPI.getStripePlans();
      setPlans(data);
    } catch {
      setPlans(null);
    }
  }, []);

  const pollCheckoutStatus = useCallback(async (sessionId) => {
    pollAttemptsRef.current += 1;
    try {
      const { data } = await landlordAPI.getStripeCheckoutStatus(sessionId);
      if (data.payment_status === 'paid') {
        setVerifying(false);
        setPaymentSuccess(true);
        toast.success('Subscription activated!');
        await fetchSubscription();
        navigate('/landlord/subscription', { replace: true });
        return;
      }
      if (data.status === 'expired' || pollAttemptsRef.current >= POLL_MAX_ATTEMPTS) {
        setVerifying(false);
        toast.error('Payment not completed. If you were charged, contact support.');
        navigate('/landlord/subscription', { replace: true });
        return;
      }
      setTimeout(() => pollCheckoutStatus(sessionId), POLL_INTERVAL_MS);
    } catch (err) {
      setVerifying(false);
      toast.error(err.response?.data?.detail || 'Could not verify payment');
    }
  }, [fetchSubscription, navigate]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('cancelled');

    (async () => {
      await Promise.all([fetchSubscription(), fetchPlans()]);
      setLoading(false);
    })();

    if (sessionId) {
      setVerifying(true);
      pollAttemptsRef.current = 0;
      pollCheckoutStatus(sessionId);
    } else if (cancelled) {
      toast.info('Checkout cancelled');
      navigate('/landlord/subscription', { replace: true });
    }
  }, [searchParams, fetchSubscription, fetchPlans, pollCheckoutStatus, navigate]);

  const handleApplyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setValidatingPromo(true);
    try {
      const { data } = await landlordAPI.validatePromoCode(code, 'monthly');
      setAppliedPromo({ code: data.code, discount_percent: data.discount_percent });
      toast.success(`Promo "${data.code}" applied — ${data.discount_percent}% off`);
    } catch (err) {
      setAppliedPromo(null);
      toast.error(err.response?.data?.detail || 'Invalid promo code');
    } finally {
      setValidatingPromo(false);
    }
  };

  const clearPromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription? You keep full access until it expires — no refund, and it simply won\'t renew.')) return;
    setCancelling(true);
    try {
      const { data } = await landlordAPI.cancelStripeSubscription();
      setSubscription(data.subscription || null);
      toast.success('Subscription cancelled. You keep access until it expires.');
      await fetchSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleSubscribe = async (planKey) => {
    setSelectedPlan(planKey);
    setProcessing(true);
    try {
      const { data } = await landlordAPI.createStripeCheckout(planKey, window.location.origin, appliedPromo?.code);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout');
        setProcessing(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create checkout session');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="subscription-loading">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isActive = subscription?.status === 'active';
  const isFreeTrial = subscription?.is_trial && !subscription?.trial_expired;
  const freeUntil = subscription?.trial_end_date ? new Date(subscription.trial_end_date) : null;
  const freeUntilDate = freeUntil
    ? freeUntil.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : 'February 1, 2027';
  const freeDaysRemaining = freeUntil
    ? Math.max(0, Math.ceil((freeUntil - new Date()) / (1000 * 60 * 60 * 24)))
    : (subscription?.days_remaining ?? 0);
  const daysRemaining = subscription?.days_remaining ?? 0;
  const currentPlan = subscription?.plan;
  const endDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  const monthlyAmount = plans?.monthly?.amount ?? 9.99;
  const annualAmount = plans?.annual?.amount ?? 95;
  const features = plans?.features?.length ? plans.features : [
    'Unlimited properties & units',
    'Unlimited tenants & property managers',
    'Payment tracking & receipts',
  ];
  const guaranteeText = plans?.guarantee_text || "Not happy in the first 30 days? Contact us and we'll process a full refund — no questions asked.";
  const pct = appliedPromo?.discount_percent || 0;

  const PLAN_CARDS = [
    {
      key: 'monthly',
      name: plans?.monthly?.name || 'Monthly',
      amount: monthlyAmount,
      period: '/ month',
      tagline: plans?.monthly?.tagline || 'One-time charge, no auto-renew',
      badge: null,
    },
    {
      key: 'annual',
      name: plans?.annual?.name || 'Annual',
      amount: annualAmount,
      period: '/ year',
      tagline: plans?.annual?.tagline || 'Pay annually and save ~20%',
      badge: plans?.annual?.badge || 'Best Value',
    },
  ];

  return (
    <div className="space-y-8" data-testid="landlord-subscription-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Subscription</h1>
        <p className="text-muted-foreground mt-1">
          One plan, everything included. Pay monthly at {money(monthlyAmount)}, or pay annually at {money(annualAmount)}. No auto-renew, no surprise charges.
        </p>
      </div>

      {/* Verifying overlay */}
      {verifying && (
        <Card className="border-2 border-[#6B8E6B]" data-testid="verifying-card">
          <CardContent className="p-6 flex items-center gap-4">
            <Loader2 className="w-6 h-6 animate-spin text-[#6B8E6B]" />
            <div>
              <p className="font-semibold">Verifying your payment…</p>
              <p className="text-sm text-muted-foreground">This usually takes a few seconds.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Free access period banner (all landlords are free until Feb 1, 2027) */}
      {isActive && isFreeTrial && (
        <Card className="border-2 border-[#6B8E6B] bg-[#6B8E6B]/5" data-testid="free-period-card">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#6B8E6B] rounded-full">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold" data-testid="free-period-title">Free until {freeUntilDate}</h3>
                    <Badge className="bg-[#6B8E6B] text-white">Free Access</Badge>
                  </div>
                  <p className="text-muted-foreground mt-1 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Every feature is unlocked — no card required. You won't be charged until {freeUntilDate}.
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-[#6B8E6B]" data-testid="days-remaining">{freeDaysRemaining}</p>
                <p className="text-sm text-muted-foreground">days of free access left</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
              Want to lock in your rate early? You can subscribe now from the plans below — your paid period will begin only after the free access ends.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active subscription banner (paid) */}
      {isActive && !isFreeTrial && (
        <Card className="border-2 border-[#6B8E6B] bg-[#6B8E6B]/5" data-testid="active-subscription-card">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#6B8E6B] rounded-full">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold">Active — {PLAN_CARDS.find((p) => p.key === currentPlan)?.name || 'Subscription'}</h3>
                    <Badge className="bg-[#6B8E6B] text-white">Active</Badge>
                  </div>
                  <p className="text-muted-foreground mt-1 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {daysRemaining} days remaining {endDate && `• ${subscription?.cancel_at_period_end ? 'Access until' : 'Renews on or before'} ${endDate}`}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-[#6B8E6B]" data-testid="days-remaining">{daysRemaining}</p>
                <p className="text-sm text-muted-foreground">days left</p>
              </div>
            </div>
            {subscription?.cancel_at_period_end ? (
              <div className="mt-4 pt-4 border-t flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300" data-testid="cancelled-notice">
                <AlertCircle className="w-4 h-4 shrink-0" />
                Cancelled — your plan won't renew. You keep full access until {endDate || 'it expires'}.
              </div>
            ) : (
              <div className="mt-4 pt-4 border-t flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-950"
                  data-testid="cancel-subscription-btn"
                >
                  {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
                  Cancel Subscription
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Annual loyalty bonus celebration */}
      {isActive && currentPlan === 'annual' && (subscription?.last_loyalty_bonus_days || 0) > 0 && (
        <Card
          className="border-2 border-[#C85A42] bg-gradient-to-r from-[#C85A42]/10 to-[#C85A42]/5"
          data-testid="loyalty-bonus-card"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 bg-[#C85A42] rounded-full shrink-0">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-[#C85A42] flex items-center gap-2">
                Thanks for renewing annually <Sparkles className="w-4 h-4" />
              </h4>
              <p className="text-sm text-muted-foreground mt-0.5">
                We added <strong className="text-foreground">+{subscription.last_loyalty_bonus_days} bonus days</strong> to your subscription as a loyalty perk for returning annual subscribers.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expired banner */}
      {subscription?.status && subscription.status !== 'active' && subscription.status !== 'none' && (
        <Card className="border-2 border-red-500 bg-red-50 dark:bg-red-950" data-testid="expired-subscription-card">
          <CardContent className="p-6 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <p className="text-red-700 dark:text-red-200">
              Your subscription is <strong>{subscription.status}</strong>. Renew below to keep access to premium features.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Promo code */}
      <Card data-testid="promo-code-card">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Ticket className="w-4 h-4 text-[#C85A42]" />
            Have a promo code?
          </div>
          {appliedPromo ? (
            <div className="flex items-center gap-2" data-testid="applied-promo">
              <Badge className="bg-[#6B8E6B] text-white">
                {appliedPromo.code} — {appliedPromo.discount_percent}% off applied
              </Badge>
              <Button variant="ghost" size="sm" onClick={clearPromo} data-testid="clear-promo-btn">
                <X className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1 max-w-sm">
              <Input
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                placeholder="Enter code"
                className="font-mono"
                data-testid="promo-input"
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyPromo(); }}
              />
              <Button variant="outline" onClick={handleApplyPromo} disabled={validatingPromo || !promoInput.trim()} data-testid="apply-promo-btn">
                {validatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plans */}
      <div className="grid md:grid-cols-2 gap-6" data-testid="plans-grid">
        {PLAN_CARDS.map((plan) => {
          const isCurrent = isActive && !isFreeTrial && currentPlan === plan.key;
          const discounted = pct > 0 ? plan.amount * (1 - pct / 100) : null;
          return (
            <Card
              key={plan.key}
              className={`relative transition-all ${plan.badge ? 'border-[#C85A42] border-2 shadow-lg' : ''} ${isCurrent ? 'ring-2 ring-[#6B8E6B]' : ''}`}
              data-testid={`plan-card-${plan.key}`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#C85A42] text-white flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    {plan.badge}
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription>{plan.tagline}</CardDescription>
                <div className="mt-4 flex items-end justify-center gap-1">
                  {discounted != null && (
                    <span className="text-2xl font-medium text-muted-foreground line-through mr-1" data-testid={`plan-original-${plan.key}`}>
                      {money(plan.amount)}
                    </span>
                  )}
                  <span className="text-5xl font-bold text-foreground" data-testid={`plan-price-${plan.key}`}>
                    {money(discounted != null ? discounted : plan.amount)}
                  </span>
                  <span className="text-muted-foreground pb-2">{plan.period}</span>
                </div>
                {discounted != null && (
                  <p className="text-sm text-[#C85A42] font-medium mt-1" data-testid={`plan-promo-note-${plan.key}`}>
                    {pct}% off with {appliedPromo.code}
                  </p>
                )}
              </CardHeader>

              <CardContent className="space-y-4">
                <Separator />
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 font-medium">
                    <InfinityIcon className="w-4 h-4 text-[#6B8E6B] shrink-0" />
                    Everything unlimited
                  </li>
                  {features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-[#6B8E6B] shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                  {plan.key === 'annual' && (
                    <li className="flex items-center gap-2 text-sm text-[#C85A42] font-medium">
                      <Gift className="w-4 h-4 text-[#C85A42] shrink-0" />
                      <span>+{LOYALTY_BONUS_DAYS} loyalty bonus days on 2nd annual renewal</span>
                    </li>
                  )}
                </ul>

                {isCurrent ? (
                  <Button className="w-full" variant="outline" disabled data-testid={`current-plan-${plan.key}`}>
                    Current Plan
                  </Button>
                ) : (
                  <Button
                    className="w-full bg-[#2F4F4F] hover:bg-[#1a3a3a]"
                    onClick={() => handleSubscribe(plan.key)}
                    disabled={processing}
                    data-testid={`subscribe-btn-${plan.key}`}
                  >
                    {processing && selectedPlan === plan.key ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Redirecting to Stripe…
                      </>
                    ) : (
                      <>Subscribe — {money(discounted != null ? discounted : plan.amount)} {plan.period}</>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Money-back guarantee */}
      <Card className="bg-muted/30" data-testid="guarantee-card">
        <CardContent className="p-6 flex items-start gap-4">
          <div className="p-3 bg-[#6B8E6B]/10 rounded-full shrink-0">
            <ShieldCheck className="w-6 h-6 text-[#6B8E6B]" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">30-Day Money-Back Guarantee</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {guaranteeText} Secure payments powered by Stripe.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Success banner */}
      {paymentSuccess && (
        <Card className="border-2 border-[#6B8E6B]" data-testid="payment-success-banner">
          <CardContent className="p-6 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-[#6B8E6B]" />
            <p className="font-medium">Payment successful — your subscription is now active.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
